import React from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface BreadcrumbItem {
  name: string;
  url: string;
}

interface BreadcrumbSchemaProps {
  items?: BreadcrumbItem[];
  baseUrl?: string;
}

/**
 * BreadcrumbSchema component adds BreadcrumbList structured data to help search engines
 * understand the site hierarchy and improve rich snippets in search results.
 */
export function BreadcrumbSchema({ items: customItems, baseUrl = '' }: BreadcrumbSchemaProps) {
  const location = useLocation();
  const { t } = useTranslation();

  const breadcrumbItems = React.useMemo((): BreadcrumbItem[] => {
    if (customItems) {
      return customItems;
    }

    const pathSegments = location.pathname.split('/').filter(Boolean);
    const items: BreadcrumbItem[] = [
      { name: t('app.title', 'Date Calculator'), url: baseUrl + '/' },
    ];

    let currentPath = '';
    pathSegments.forEach((segment, _index) => {
      currentPath += `/${segment}`;

      // Get page title from translation or use segment
      const routeKey = segment.replace(/-([a-z])/g, (_match, char) => char.toUpperCase());
      const name = t(`${routeKey}.title`, t(`nav.${routeKey}`, segment));

      items.push({
        name,
        url: baseUrl + currentPath,
      });
    });

    return items;
  }, [baseUrl, customItems, location.pathname, t]);

  const structuredData = React.useMemo(
    () => ({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumbItems.map((item, idx) => ({
        '@type': 'ListItem',
        position: idx + 1,
        name: item.name,
        item: item.url,
      })),
    }),
    [breadcrumbItems]
  );

  React.useEffect(() => {
    let script = document.getElementById('breadcrumb-schema') as HTMLScriptElement | null;

    if (!script) {
      script = document.createElement('script');
      script.id = 'breadcrumb-schema';
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }

    script.textContent = JSON.stringify(structuredData);

    return () => {
      script?.remove();
    };
  }, [structuredData]);

  return null;
}
